import { Component } from '@theme/component';

/**
 * Gates the cart's checkout buttons behind a terms and privacy checkbox.
 *
 * Consent lives in the cart rather than at checkout because Shopify only allows
 * additions to the thank-you and order-status pages on non-Plus plans.
 *
 * Scopes every lookup to the surrounding `.cart__ctas`, so the cart page and the
 * cart drawer each gate their own buttons even though both are on the page.
 *
 * @typedef {Object} CartConsentRefs
 * @property {HTMLInputElement} checkbox - The consent checkbox.
 *
 * @extends {Component<CartConsentRefs>}
 */
class CartConsent extends Component {
  /*
   * Deliberately no `requiredRefs`: a missing ref makes the base class throw out
   * of `connectedCallback`, which would leave the gate half-wired and the
   * checkout button enabled. Reading the checkbox defensively instead means a
   * broken render disables checkout rather than silently waving it through.
   */

  connectedCallback() {
    super.connectedCallback();
    this.toggle();
  }

  toggle() {
    const ctas = this.closest('.cart__ctas');
    if (!ctas) return;

    const agreed = this.refs.checkbox?.checked === true;
    // An empty cart stays un-checkoutable however the box is set.
    const cartEmpty = this.dataset.cartEmpty === 'true';

    this.dataset.agreed = String(agreed);
    ctas.classList.toggle('cart__ctas--consent-pending', !agreed);

    const checkout = ctas.querySelector('button[name="checkout"]');
    if (checkout instanceof HTMLButtonElement) {
      checkout.disabled = cartEmpty || !agreed;
    }
  }
}

if (!customElements.get('cart-consent')) {
  customElements.define('cart-consent', CartConsent);
}
